// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

// eslint-disable-next-line spire/fenced-imports
import { hideAll } from "tippy.js";

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Logger from "@insite/client-framework/Logger";
import siteMessage from "@insite/client-framework/SiteMessage";
import {
    getCurrentCartState,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button/Button";
import LoadingOverlay, { LoadingOverlayProps } from "@insite/mobius/LoadingOverlay/LoadingOverlay";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { HasHistory, useHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import FancyScroll, { FancyScrollRefProps } from "@tps/App/Components/Common/FancyScroll";
import { getCartLineErrorMessages } from "@tps/App/Components/MiniCart/MiniCart.helpers";
import MiniCartLineCard from "@tps/App/Components/MiniCart/MiniCartLineCard";
import Notify from "@tps/App/Components/Notify/Notify";
import { CustomAppState } from "@tps/App/Store/Reducers";
import { color, styledScrollbar, xScreen } from "@tps/XDL/Utilities";
import React, { FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

export const cartLineTippyContentTopPadding = 20;

interface OwnProps {
    isPopupShown?: boolean;
}

const mapStateToProps = (state: CustomAppState) => {
    const cartState = getCurrentCartState(state);

    return {
        shouldLoadCart: !cartState.isLoading && (!cartState.value || (cartState.value.lineCount && !cartState.value.cartLines)),
        totalCountDisplay: getCurrentCartState(state).value?.totalCountDisplay || 0,
        orderGrandTotalDisplay: getCurrentCartState(state).value?.orderGrandTotalDisplay,
        cartPageUrl: getPageLinkByPageType(state, "CartPage")?.url,
        cart: cartState.value,
        isUpdatingCartLine: state.pages.cart.isUpdatingCartLine,
        isLoading: state.data.carts.isLoading,
        cartLoadingOverlay: state.xCommon.loadingOverlay,
    };
};

const mapDispatchToProps = {
    loadCurrentCart,
    updateCartLine,
    removeCartLine,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

interface MiniCartStyles {
    centerWrapper?: InjectableCss;
    notifyWrapper?: InjectableCss;
    footerWrapper?: InjectableCss;
    infoItemsWrapper?: InjectableCss;
    noCartLinesNotify?: TypographyProps;
    cartLinesWrapper?: InjectableCss;
    productAvailabilityStyles?: ProductAvailabilityStyles;
    actionWrapper?: InjectableCss;
    checkoutButton?: ButtonPresentationProps;
    viewCartButton?: ButtonPresentationProps;
    loadingOverlay?: LoadingOverlayProps;
    fancyScrollStyles?: InjectableCss;
    infoLabel?: TypographyProps;
    infoValue?: TypographyProps;
}

const miniCartStyles: MiniCartStyles = {
    footerWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            padding: 16px;
            width: 100%;
            row-gap: 12px;
            border-top: 1px solid ${color("common.border")};
        `,
    },
    centerWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            width: 100%;
            padding: 20px;
        `,
    },
    notifyWrapper: {
        css: css`
            padding: 16px;
        `,
    },
    infoItemsWrapper: {
        css: css`
            margin-bottom: 0;
            display: flex;
            justify-content: space-between;
            width: 100%;
        `,
    },
    cartLinesWrapper: {
        css: css`
            position: relative;
            max-height: 370px;
            min-width: 440px;
            overflow-x: hidden;
            ${styledScrollbar(4)}
        `,
    },
    productAvailabilityStyles: {
        container: {
            gap: 0,
            css: css`
                width: auto;
                flex: 1 0 auto;
            `,
        },
    },
    actionWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
            position: relative;
            ${xScreen("lte", "xs")} {
                flex-direction: column;
            }
        `,
    },
    viewCartButton: {
        sizeVariant: "large",
        mergeCss: true,
        css: css`
            width: 100%;
            min-height: 40px;
        `,
    },
    fancyScrollStyles: {
        css: css`
            span[class^="ScrollIndicator"] {
                height: 32px;
            }
            margin-bottom: 1px;
            & > span[direction="down"] {
                bottom: -1px;
            }
            & > span[direction="up"] {
                top: -1px;
            }
        `,
    },
    infoLabel: {
        size: 14,
        lineHeight: "22px",
        css: css`
            margin-right: 8px;
        `,
    },
    infoValue: {
        size: 18,
        lineHeight: "24px",
        weight: "bold",
    }
};

const styles = miniCartStyles;

const MiniCart: FC<Props> = ({
    cart,
    cartPageUrl,
    totalCountDisplay,
    orderGrandTotalDisplay,
    removeCartLine,
    updateCartLine,
    isUpdatingCartLine,
    isLoading,
    isPopupShown,
    cartLoadingOverlay,
}) => {
    const history = useHistory();
    const parentBoxRef = React.createRef<HTMLDivElement>();
    const fancyScrollRef = React.createRef<FancyScrollRefProps>();

    useEffect(() => {
        if (isPopupShown && fancyScrollRef.current) {
            fancyScrollRef.current.updateScrollable();
        }
    }, [isPopupShown]);

    const hideTippy = () => {
        setTimeout(() => {
            hideAll();
        }, 500);
    };

    const qtyOrderedChangeHandler = (cartLine: CartLineModel, qtyOrdered: number) => {
        if (qtyOrdered <= 0) {
            if (!cart!.cartLines?.some(line => line.id !== cartLine.id && line.properties.warehouseId === cartLine.properties.warehouseId)) {
                // this means its the only cartLine in the warehouse, so we need to remove the warehouse shipping info
                removeCartLine({ cartLineId: cartLine.id, warehouseId: cartLine.properties.warehouseId } as any);
            } else {
                removeCartLine({ cartLineId: cartLine.id });
            }
        } else {
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    qtyOrdered,
                },
            });
        }
    };

    const removeCartLineClickHandler = (cartLine: CartLineModel) => {
        if (!cart!.cartLines?.some(line => line.id !== cartLine.id && line.properties.warehouseId === cartLine.properties.warehouseId)) {
            // this means its the only cartLine in the warehouse, so we need to remove the warehouse shipping info
            removeCartLine({ cartLineId: cartLine.id, warehouseId: cartLine.properties.warehouseId } as any);
        } else {
            removeCartLine({ cartLineId: cartLine.id });
        }
    };

    const handleViewCarClick = () => {
        if (!cartPageUrl) {
            Logger.error(`No Cart Page Url found`);
            return;
        }
        hideTippy();
        history.push(cartPageUrl);
    };

    if (!cart && isLoading) {
        return (
            <StyledWrapper {...styles.centerWrapper}>
                <LoadingSpinner />
            </StyledWrapper>
        );
    }

    if (!cart) {
        return null;
    }

    const cartLines = cart.cartLines ?? [];
    const isLoadingCart = isLoading[cart.id];

    if (!cartLines?.length) {
        return (
            <StyledWrapper {...styles.notifyWrapper}>
                <Notify type="info">
                    {siteMessage("Custom_Empty_Cart_Message")}
                </Notify>
            </StyledWrapper>
        );
    }

    return (
        <LoadingOverlay
            {...styles.loadingOverlay}
            loading={isLoadingCart || isUpdatingCartLine || cartLoadingOverlay?.includes("cartLines")}
        >
            <FancyScroll refElement={parentBoxRef} ref={fancyScrollRef} extendedStyles={styles.fancyScrollStyles}>
                <StyledWrapper {...styles.cartLinesWrapper} ref={parentBoxRef} data-test-selector="cartLines-container">
                    {cartLines?.map(cartLine => {
                        const errorMessages = getCartLineErrorMessages(cartLines, cartLine);
                        return (
                            <MiniCartLineCard
                                key={`miniCartLine_${cartLine.id}`}
                                cartLine={cartLine}
                                cart={cart}
                                hideTippy={hideTippy}
                                qtyOrderedChangeHandler={qtyOrderedChangeHandler}
                                removeCartLineClickHandler={removeCartLineClickHandler}
                                errorMessages={errorMessages}
                            />
                        );
                    })}
                </StyledWrapper>
            </FancyScroll>
            <StyledWrapper {...styles.footerWrapper}>
                <StyledWrapper {...styles.infoItemsWrapper}>
                    <StyledWrapper>
                        <Typography {...styles.infoLabel}>{`${translate("QTY")}:`}</Typography>
                        <Typography {...styles.infoValue}>{totalCountDisplay}</Typography>
                    </StyledWrapper>
                    <StyledWrapper>
                        <Typography {...styles.infoLabel}>{`${translate("Total")}:`}</Typography>
                        <Typography {...styles.infoValue}>{orderGrandTotalDisplay}</Typography>
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.actionWrapper}>
                    <Button {...styles.viewCartButton} aria-label={translate("View Cart")} onClick={handleViewCarClick}>
                        {translate("View Cart")}
                    </Button>
                </StyledWrapper>
            </StyledWrapper>
        </LoadingOverlay>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(MiniCart));
